import { useEffect, useRef } from "react";
import { ApiError, request } from "../api/client";
import { useMe } from "../api/queries";
import type { PlayerPrefs, PlayerResume } from "../api/types";
import {
  currentAudibleItem,
  currentFilter,
  currentPlaybackObservation,
  playbackMediaUs,
  sourceMsAt,
} from "../player/useAudioSession";
import { type PlaybackSource, type PlayerState, usePlayer } from "./player";
import { useZone } from "../time/useZone";
import { nativeRuntime } from "../platform/native";

type ResumeTarget = PlayerResume | { mode: "legacy"; position_ms: number };

export interface Resume {
  volume: number;
  muted: boolean;
  positionMs: number;
  atLive: boolean;
  resume: ResumeTarget | null;
}

const KEY = "jtarchive.player";
const SAVE_MS = 5_000;
let remembered: ResumeTarget | null = null;
let rememberedSource: PlaybackSource | null = null;
let intentRevision = 0;
let hydrationRevision = 0;

function changedIntent(state: PlayerState, previous: PlayerState): boolean {
  return (
    state.source !== previous.source ||
    state.seekToken !== previous.seekToken ||
    state.wantsPlayback !== previous.wantsPlayback ||
    state.selectedId !== previous.selectedId ||
    state.volume !== previous.volume ||
    state.muted !== previous.muted
  );
}

usePlayer.subscribe((state, previous) => {
  if (changedIntent(state, previous)) intentRevision += 1;
});

function validDate(value: unknown): value is string {
  return typeof value === "string" && Number.isFinite(Date.parse(value));
}

function savedFilter(raw: unknown): Record<string, string | number | boolean> | undefined {
  if (typeof raw !== "object" || raw === null || Array.isArray(raw)) return undefined;
  const entries = Object.entries(raw).filter(([, value]) => value !== null);
  if (
    entries.some(
      ([, value]) =>
        typeof value !== "string" &&
        typeof value !== "boolean" &&
        !(typeof value === "number" && Number.isFinite(value)),
    )
  ) {
    return undefined;
  }
  return Object.fromEntries(entries);
}

function sanitizeTarget(raw: unknown): ResumeTarget | null {
  if (typeof raw !== "object" || raw === null) return null;
  const value = raw as Record<string, unknown>;
  if (value.mode === "legacy" && typeof value.position_ms === "number" && value.position_ms >= 0) {
    return { mode: "legacy", position_ms: value.position_ms };
  }
  if (value.mode === "live" && (value.source_at === null || validDate(value.source_at))) {
    return { mode: "live", source_at: value.source_at };
  }
  if (value.mode === "dvr" && validDate(value.source_at)) {
    return { mode: "dvr", source_at: value.source_at };
  }
  if (
    value.mode === "archive" &&
    (value.presentation_id == null ||
      (typeof value.presentation_id === "string" && value.presentation_id.length > 0)) &&
    (value.segment_id === null ||
      (Number.isInteger(value.segment_id) && (value.segment_id as number) > 0)) &&
    typeof value.item_position_us === "number" &&
    Number.isSafeInteger(value.item_position_us) &&
    value.item_position_us >= 0 &&
    (value.source_at === null || validDate(value.source_at)) &&
    (value.segment_id !== null || value.source_at !== null) &&
    (value.filter === undefined || savedFilter(value.filter) !== undefined)
  ) {
    return {
      mode: "archive",
      presentation_id: (value.presentation_id as string | null | undefined) ?? null,
      segment_id: value.segment_id as number | null,
      item_position_us: value.item_position_us,
      source_at: value.source_at,
      ...(value.filter === undefined ? {} : { filter: savedFilter(value.filter) }),
    };
  }
  return null;
}

export function sanitize(raw: unknown): Partial<Resume> {
  if (typeof raw !== "object" || raw === null || Array.isArray(raw)) return {};
  const value = raw as Record<string, unknown>;
  const out: Partial<Resume> = {};
  if (typeof value.volume === "number" && Number.isFinite(value.volume)) {
    out.volume = Math.min(1, Math.max(0, value.volume));
  }
  if (typeof value.muted === "boolean") out.muted = value.muted;
  if (typeof value.positionMs === "number" && Number.isFinite(value.positionMs)) {
    out.positionMs = value.positionMs;
  }
  if (typeof value.atLive === "boolean") out.atLive = value.atLive;
  const resume = sanitizeTarget(value.resume);
  if (resume !== null) out.resume = resume;
  return out;
}

export function loadResume(userId?: number): Partial<Resume> {
  try {
    const key = userId === undefined ? KEY : `${KEY}:${userId}`;
    return sanitize(JSON.parse(window.localStorage.getItem(key) ?? "null"));
  } catch {
    return {};
  }
}

function legacyResume(
  positionMs: number | undefined,
  atLive: boolean | undefined,
): ResumeTarget | null {
  if (atLive === true) return { mode: "live", source_at: null };
  if (atLive === false && positionMs !== undefined) {
    return { mode: "legacy", position_ms: positionMs };
  }
  return null;
}

export function resumeSource(target: ResumeTarget): PlaybackSource {
  switch (target.mode) {
    case "archive":
      return {
        kind: "presentation",
        presentationId: target.presentation_id ?? null,
        segmentId: target.segment_id,
        itemPositionUs: target.item_position_us,
        sourceAt: target.source_at,
        ...(target.filter === undefined ? {} : { filter: target.filter }),
      };
    case "dvr":
      return { kind: "dvr", sourceAtMs: Date.parse(target.source_at) };
    case "legacy":
      return { kind: "window", startMs: target.position_ms };
    case "live":
      return { kind: "live" };
  }
}

function applyResume(state: Partial<Resume>): void {
  const target = state.resume ?? legacyResume(state.positionMs, state.atLive);
  remembered = target;
  usePlayer.setState({
    ...(state.volume === undefined ? {} : { volume: state.volume }),
    ...(state.muted === undefined ? {} : { muted: state.muted }),
    ...(state.positionMs === undefined ? {} : { positionMs: state.positionMs }),
    source: target === null ? null : resumeSource(target),
    selectedId: target?.mode === "archive" ? target.segment_id : null,
    positionAt: null,
    seekRequest: null,
    atLive: target?.mode === "live",
    wantsPlayback: false,
    playing: false,
    loading: false,
    error: null,
  });
  rememberedSource = usePlayer.getState().source;
}

export function hydratePlayer(): void {
  applyResume(loadResume());
  hydrationRevision = intentRevision;
}

export function fromWire(prefs: PlayerPrefs): Resume {
  return {
    volume: prefs.volume,
    muted: prefs.muted,
    positionMs: prefs.position_ms,
    atLive: prefs.at_live,
    resume: sanitizeTarget(prefs.resume) ?? legacyResume(prefs.position_ms, prefs.at_live),
  };
}

function observedResume(): PlayerResume | null {
  const state = usePlayer.getState();
  const current = currentPlaybackObservation();
  const mediaUs = current ? playbackMediaUs(current) : null;
  const sourceAt = current && mediaUs !== null ? sourceMsAt(current.presentation, mediaUs) : null;
  const sourceAtWire = sourceAt === null ? null : new Date(sourceAt).toISOString();

  if (state.source?.kind === "live" && state.atLive) {
    return { mode: "live", source_at: sourceAtWire };
  }
  if (state.source?.kind === "dvr") {
    return {
      mode: "dvr",
      source_at: sourceAtWire ?? new Date(state.source.sourceAtMs).toISOString(),
    };
  }
  if (!current || mediaUs === null) return null;
  if (current.presentation.mode === "live") {
    return sourceAtWire === null ? null : { mode: "dvr", source_at: sourceAtWire };
  }
  const audible = currentAudibleItem();
  if (!audible) return null;
  return {
    mode: "archive",
    presentation_id: current.presentation.id,
    segment_id: audible.item.segment_id,
    item_position_us: audible.itemPositionUs,
    source_at: sourceAtWire,
    ...(remembered?.mode === "archive" && remembered.filter ? { filter: remembered.filter } : {}),
  };
}

function currentResume(zone: string): Resume {
  const state = usePlayer.getState();
  if (state.source !== rememberedSource) {
    rememberedSource = state.source;
    const source = state.source;
    remembered =
      source === null
        ? null
        : source.kind === "presentation"
          ? {
              mode: "archive",
              presentation_id: source.presentationId,
              segment_id: source.segmentId,
              item_position_us: source.itemPositionUs,
              source_at: source.sourceAt,
              ...(source.filter === undefined ? {} : { filter: source.filter }),
            }
          : source.kind === "live"
            ? { mode: "live", source_at: null }
            : source.kind === "dvr"
              ? {
                  mode: "dvr",
                  source_at: new Date(source.sourceAtMs).toISOString(),
                }
              : {
                  mode: "archive",
                  presentation_id: null,
                  segment_id: source.kind === "segment" ? source.segment.id : null,
                  item_position_us: 0,
                  source_at: new Date(state.positionMs).toISOString(),
                  filter: currentFilter(zone),
                };
  }
  const target = state.seekRequest?.target;
  if (target) {
    const current = currentPlaybackObservation();
    const sourceMs =
      target.kind === "source"
        ? target.atMs
        : current
          ? sourceMsAt(current.presentation, target.mediaUs)
          : null;
    const item =
      target.kind === "media"
        ? current?.presentation.map.items.find(
            (row) =>
              row.media_start_us <= target.mediaUs &&
              (row.media_end_us === null || target.mediaUs < row.media_end_us),
          )
        : null;
    if (sourceMs !== null) {
      const at = new Date(sourceMs).toISOString();
      remembered =
        state.source?.kind === "live" || state.source?.kind === "dvr"
          ? { mode: "dvr", source_at: at }
          : {
              mode: "archive",
              presentation_id: current?.presentation.id ?? null,
              segment_id:
                target.kind === "source"
                  ? (target.segmentId ??
                    (remembered?.mode === "archive" ? remembered.segment_id : null))
                  : (item?.segment_id ?? null),
              item_position_us:
                target.kind === "media" && item
                  ? Math.max(0, target.mediaUs - item.media_start_us)
                  : 0,
              source_at: at,
              filter: remembered?.mode === "archive" ? remembered.filter : currentFilter(zone),
            };
    }
  }
  if (state.positionAt !== null && !state.loading && state.seekRequest === null) {
    const observed = observedResume();
    if (observed !== null) remembered = observed;
  }
  return {
    volume: state.volume,
    muted: state.muted,
    positionMs: state.positionMs,
    atLive: state.atLive,
    resume: remembered,
  };
}

function toWire(state: Resume): PlayerPrefs {
  return {
    volume: state.volume,
    muted: state.muted,
    position_ms: Math.max(0, Math.round(state.positionMs)),
    at_live: state.atLive,
    resume: state.resume?.mode === "legacy" ? null : state.resume,
  };
}

function targetKey(state: Partial<Resume>): string | null {
  const target = state.resume ?? legacyResume(state.positionMs, state.atLive);
  if (target === null) return null;
  switch (target.mode) {
    case "live":
      return "live";
    case "legacy":
      return JSON.stringify(["time", target.position_ms]);
    case "dvr":
      return JSON.stringify(["time", Date.parse(target.source_at)]);
    case "archive":
      return JSON.stringify([
        "archive",
        target.segment_id,
        target.item_position_us,
        target.source_at === null ? null : Date.parse(target.source_at),
        Object.entries({ joined: true, ...target.filter })
          .filter(
            ([key, value]) =>
              value !== null && (key !== "listened_max" || target.filter?.listened === "no"),
          )
          .sort(([a], [b]) => a.localeCompare(b)),
      ]);
  }
}

export function useResumeSync(): void {
  const { data: me } = useMe();
  const zone = useZone().name;
  const userId = me?.id ?? null;
  const unresolved = me === undefined;
  const adopted = useRef<number | null | undefined>(undefined);
  const acknowledged = useRef("");
  const expectedRevision = useRef("");
  const browser = nativeRuntime() === null;

  useEffect(() => {
    if (!browser) return;
    if (me === undefined || adopted.current === userId) return;
    const switchingAccount = adopted.current !== undefined && adopted.current !== userId;
    adopted.current = userId;
    const remote = me?.prefs ? fromWire(me.prefs) : null;
    acknowledged.current = remote === null ? "" : JSON.stringify(toWire(remote));
    expectedRevision.current = me?.prefs_revision ?? "";
    const local = userId === null ? loadResume() : loadResume(userId);
    const unclaimed = loadResume();
    const matches =
      remote !== null &&
      targetKey(unclaimed) !== null &&
      targetKey(unclaimed) === targetKey(remote);
    const initial =
      targetKey(local) !== null
        ? local
        : matches
          ? unclaimed
          : (remote ?? { volume: 1, muted: false, positionMs: Date.now(), atLive: true });
    if (switchingAccount || intentRevision === hydrationRevision) applyResume(initial);
  }, [browser, me, userId]);

  useEffect(() => {
    if (!browser) return;
    const key = userId === null ? KEY : `${KEY}:${userId}`;
    let latest = JSON.stringify(toWire(currentResume(zone)));
    let written = acknowledged.current || latest;
    let lastSample = latest;
    let inFlight: AbortController | null = null;
    let sending = "";
    let finalPending = false;
    let disposed = false;
    const saveLocal = () => {
      const state = currentResume(zone);
      latest = JSON.stringify(toWire(state));
      try {
        window.localStorage.setItem(key, JSON.stringify(state));
      } catch {
        // Playback must survive unavailable browser storage.
      }
      lastSample = latest;
    };
    const flush = (final = false) => {
      if (disposed || unresolved || userId === null) return;
      if (inFlight) {
        if (final && latest !== sending) finalPending = true;
        return;
      }
      if (latest === written) return;
      const encoded = latest;
      const abort = new AbortController();
      inFlight = abort;
      sending = encoded;
      const prefs = JSON.parse(encoded) as PlayerPrefs;
      void request<{ prefs_revision: string }>("/api/auth/me/prefs", {
        method: "PUT",
        body: JSON.stringify({
          ...prefs,
          expected_revision: expectedRevision.current,
        }),
        keepalive: true,
        signal: abort.signal,
      })
        .then((saved) => {
          expectedRevision.current = saved.prefs_revision;
          written = encoded;
        })
        .catch((error: unknown) => {
          if (error instanceof ApiError && error.status === 409) {
            const conflict = error.data as {
              prefs?: PlayerPrefs | null;
              prefs_revision?: unknown;
            } | null;
            if (typeof conflict?.prefs_revision === "string") {
              expectedRevision.current = conflict.prefs_revision;
              acknowledged.current =
                conflict.prefs === null || conflict.prefs === undefined
                  ? ""
                  : JSON.stringify(conflict.prefs);
              written = latest;
            }
          }
        })
        .finally(() => {
          inFlight = null;
          if (!disposed && finalPending) {
            finalPending = false;
            flush();
          }
        });
    };
    const sample = () => {
      const encoded = JSON.stringify(toWire(currentResume(zone)));
      if (encoded !== lastSample) saveLocal();
      flush();
    };
    const unsubscribe = usePlayer.subscribe((state, previous) => {
      if (changedIntent(state, previous)) saveLocal();
      if (previous.wantsPlayback && !state.wantsPlayback && state.source !== null) flush(true);
    });
    const leave = () => {
      saveLocal();
      flush(true);
    };
    if (!unresolved) saveLocal();
    const timer = window.setInterval(sample, SAVE_MS);
    window.addEventListener("pagehide", leave);
    return () => {
      disposed = true;
      unsubscribe();
      window.clearInterval(timer);
      window.removeEventListener("pagehide", leave);
      inFlight?.abort();
    };
  }, [browser, userId, unresolved, zone]);
}
